import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import VersionPreviewModal from '@zeus/core/modals/VersionPreviewModal';
import { readmeHistoryAdapter } from '_/common/adapters/readmeHistory.adapter';
import ReadmeHistory from '_/common/components/readme/ReadmeHistory';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useSourceContext } from '_/contexts/SourceContext';
import {
  useSourceReadmeVersions,
  SOURCE_QUERY_KEY,
  SOURCE_README_QUERY_KEY,
  SOURCE_README_VERSIONS_QUERY_KEY,
} from '_/hooks/useSources';
import zeusSdk from '_/utils/SDKs';
import React, { useState, ReactElement, useMemo, useEffect } from 'react';

interface ReadmeVersion {
  after: {
    content: string;
  };
}

function SourceReadmeHistory(): ReactElement {
  const history = useNavigate();
  const queryClient = useQueryClient();
  const { selectedSource: source, selectedSourceSlug } = useSourceContext();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProjectSlug, selectedProject } = useProject();
  const { selectedCollectionSlug, selectedCollection } = useCollection();
  const { setBreadcrumbs } = useTopHeader();

  const { data: readmeVersionsData } = useSourceReadmeVersions(source?.id);

  // Adapt versions data to ReadmeHistory format
  const readmeVersions = useMemo(() => readmeHistoryAdapter(readmeVersionsData?.items || []), [readmeVersionsData]);

  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<ReadmeVersion | Record<string, never>>({});

  const restoreToPreviousVersion = async (version: ReadmeVersion) => {
    if (!source) return;

    const restoredReadme = version.after.content;

    try {
      // Update readme as a child resource - this creates a new version
      await zeusSdk.sources.updateChild(source.id, 'readme', { content: restoredReadme });

      // Invalidate source, readme and versions cache
      void queryClient.invalidateQueries({ queryKey: SOURCE_QUERY_KEY(source.id) });
      void queryClient.invalidateQueries({ queryKey: SOURCE_README_QUERY_KEY(source.id) });
      void queryClient.invalidateQueries({ queryKey: SOURCE_README_VERSIONS_QUERY_KEY(source.id) });

      // Navigate back to source
      void history({
        to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${source.slug}`,
      });

      if (previewModalVisible) {
        setPreviewModalVisible(false);
        setSelectedVersion({});
      }
    } catch (error) {
      console.error('Error restoring readme version:', error);
    }
  };

  const previewPreviousVersion = (version?: ReadmeVersion) => {
    if (version) {
      setSelectedVersion(version);
    }
    setPreviewModalVisible(!previewModalVisible);
  };

  // Set up breadcrumbs
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
    ];

    if (selectedCollection) {
      breadcrumbs.push({
        title: selectedCollection.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/home`,
      });
    }

    if (source) {
      breadcrumbs.push(
        {
          title: 'Sources',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources`,
        },
        {
          title: source.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/home`,
        },
        { title: 'Readme History' },
      );
    }

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    setBreadcrumbs,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    selectedCollection,
    selectedCollectionSlug,
    source,
    selectedSourceSlug,
  ]);

  if (!source) return null;

  return (
    <>
      <ReadmeHistory
        readmeVersions={readmeVersions}
        onRestoreClick={restoreToPreviousVersion}
        onPreviewClick={previewPreviousVersion}
      />

      <VersionPreviewModal
        open={previewModalVisible}
        versionData={selectedVersion}
        onRestoreClick={restoreToPreviousVersion}
        onCancelClick={() => previewPreviousVersion()}
      />
    </>
  );
}

export default SourceReadmeHistory;
